/**
 * O estado "há uma versão nova esperando", separado do registro do service
 * worker.
 *
 * Existe em dois arquivos de propósito. Este não importa
 * `virtual:pwa-register` — o módulo virtual só existe sob o Vite, e puxá-lo
 * para dentro da árvore de componentes tornaria a tela de atualização
 * impossível de testar. Aqui fica o estado; em `pwa.ts`, a ponte com o
 * navegador.
 *
 * ## O defeito que isto conserta
 *
 * `registerType: 'prompt'` (vite.config.ts) instala o service worker novo e o
 * deixa em **waiting**, esperando aprovação. O `main.tsx` chamava
 * `registerSW({ immediate: true })` **sem nenhum callback**, então a aprovação
 * nunca era pedida e o app novo nunca entrava.
 *
 * O comentário que estava lá dizia que "a versão nova entra na próxima
 * abertura, comportamento padrão do navegador". **Isso é falso**: um service
 * worker em waiting só assume quando TODOS os clientes da origem se fecham.
 * Recarregar a aba não basta — há sobreposição entre o cliente velho e o novo.
 * Num PWA instalado na tela de início, que ninguém "fecha", a versão publicada
 * pode nunca chegar. Foi o que aconteceu: um deploy no ar e o navegador
 * servindo um bundle de dias antes.
 */

type Ouvinte = () => void;

const ouvintes = new Set<Ouvinte>();

/** Função que manda o SW em espera assumir e recarrega a página. */
let aplicar: (() => void) | null = null;

export function atualizacaoPendente(): boolean {
  return aplicar !== null;
}

/**
 * Chamado pela ponte (`pwa.ts`) quando o navegador avisa que há versão nova.
 *
 * Recebe a função de aplicar em vez de um booleano porque quem sabe recarregar
 * é o `updateSW` do plugin — guardar só a flag obrigaria a tela a redescobrir o
 * registro para agir.
 */
export function anunciarAtualizacao(aplicarAgora: () => void): void {
  aplicar = aplicarAgora;
  for (const ouvinte of ouvintes) ouvinte();
}

export function assinarAtualizacao(ouvinte: Ouvinte): () => void {
  ouvintes.add(ouvinte);
  return () => {
    ouvintes.delete(ouvinte);
  };
}

/**
 * Aplica a atualização: `skipWaiting` no service worker e recarga da página.
 *
 * A recarga é feita pelo `updateSW(true)` do plugin, que espera o
 * `controllerchange` antes de trocar — recarregar por conta própria pegaria o
 * cliente ainda sob o worker antigo e serviria o bundle velho de novo.
 */
export function aplicarAtualizacao(): void {
  aplicar?.();
}

/** Só para teste: devolve o módulo ao estado de "nada pendente". */
export function limparAtualizacao(): void {
  aplicar = null;
  for (const ouvinte of ouvintes) ouvinte();
}
